import React, { useState } from 'react';
import PublicLayout from '../components/PublicLayout';
import { Search, ChevronDown, ChevronUp, HelpCircle, Mail } from 'lucide-react';

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);

  const faqs = [
    {
      category: "Getting Started",
      questions: [
        {
          q: "How do I sign up for Success Club?",
          a: "Click the 'Get Started' button on our homepage or pricing page. Choose your membership type, complete the registration form, and set up your payment method. You'll receive instant access to the platform."
        },
        {
          q: "What's the difference between membership tiers?",
          a: "Member tier ($15/month) offers access to all events and features. Host tier (FREE) allows you to create events and build your community. Event Host provides bulk access for conference attendees."
        },
        {
          q: "Can I switch membership plans?",
          a: "Yes! You can upgrade or downgrade your membership at any time from your account settings. Changes take effect at the start of your next billing cycle."
        }
      ]
    },
    {
      category: "Events & Challenges",
      questions: [
        {
          q: "How do I RSVP for events?",
          a: "Navigate to the Events page, browse upcoming events, and click 'RSVP' on any event that interests you. You'll receive confirmation and reminder emails before the event."
        },
        {
          q: "What are challenges and how do they work?",
          a: "Challenges are goal-oriented activities created by hosts or admins. Complete challenges to earn points, improve your skills, and climb the leaderboard."
        },
        {
          q: "How do I earn points?",
          a: "Earn points by completing challenges, attending events, giving social media boosts to other members, and engaging with the community."
        }
      ]
    },
    {
      category: "Community & Support",
      questions: [
        {
          q: "How do I contact support?",
          a: "Email us at info@oursuccessclub.com or use the Contact Us page. We typically respond within 24 hours during business days."
        },
        {
          q: "Can I message other members?",
          a: "Yes! Use our built-in chat system to connect with other members, hosts, and admins. Access messages from your dashboard."
        },
        {
          q: "Is my data secure?",
          a: "Absolutely. We use industry-standard encryption and security measures. Visit our Security page for more details."
        }
      ]
    }
  ];

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const filteredFaqs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(faq =>
      faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.a.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <PublicLayout>
      <div className="public-page">
        {/* Hero Section */}
        <div className="public-page-header">
          <h1>Help Center</h1>
          <p>
            Find answers to common questions about Success Club.
          </p>
        </div>

        <div className="public-page-content">
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-12">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 transform -translate-y-1/2 w-6 h-6 text-gray-500 pointer-events-none z-10" />
              <input
                type="text"
                placeholder="Search for help..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-16 pr-6 py-5 rounded-xl text-gray-900 font-medium"
                style={{
                  background: '#ffffff',
                  border: '2px solid #e5e7eb',
                  fontSize: '1.1rem',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  transition: 'all 0.3s ease'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#D4AF37';
                  e.target.style.boxShadow = '0 8px 20px rgba(212, 175, 55, 0.15)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
                }}
              />
            </div>
          </div>

          {/* FAQs */}
          <div className="max-w-4xl mx-auto">
            {filteredFaqs.map((category, catIndex) => (
              <div key={catIndex} className="mb-12">
                <h2 style={{fontFamily: 'var(--font-heading)', fontSize: '2.5rem', marginBottom: '1.5rem'}}>{category.category}</h2>
                <div className="space-y-4">
                  {category.questions.map((faq, faqIndex) => {
                    const globalIndex = `${catIndex}-${faqIndex}`;
                    const isExpanded = expandedFaq === globalIndex;
                    return (
                      <div key={faqIndex} className="dashboard-card">
                        <button
                          onClick={() => toggleFaq(globalIndex)}
                          className="w-full flex items-center justify-between text-left"
                          style={{background: 'transparent', border: 'none', padding: 0, cursor: 'pointer'}}
                        >
                          <div className="flex items-center gap-3">
                            <HelpCircle className="w-5 h-5" style={{color: 'var(--gold)'}} />
                            <span style={{fontFamily: 'var(--font-heading)', fontSize: '1.25rem', fontWeight: 600}}>{faq.q}</span>
                          </div>
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                        {isExpanded && (
                          <p style={{color: 'var(--muted)', marginTop: '1rem', lineHeight: '1.8'}}>{faq.a}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="text-center rounded-2xl p-12 max-w-4xl mx-auto" style={{background: 'linear-gradient(135deg, #D4AF37 0%, #C29A4B 100%)', color: '#fff'}}>
            <Mail className="w-16 h-16 mx-auto mb-4" style={{color: '#fff'}} />
            <h2 className="mb-4" style={{color: '#fff', fontFamily: 'var(--font-heading)', fontSize: '3rem'}}>Still Need Help?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto" style={{color: 'rgba(255,255,255,0.9)'}}>
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <a 
              href="/contact"
              className="bg-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-colors inline-block no-underline"
              style={{color: '#1A1A1A'}}
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};

export default HelpCenter;